import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Shop2OutlinedIcon from '@mui/icons-material/Shop2Outlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import is from '@sindresorhus/is';
import { Fragment, useContext, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { TalentDirectoryBarChart } from './TalentDirectoryBarChart';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { and, equals } from '../../../../../shared/source/data-contract/filter';
import { useLearningOpportunityFilterQuery } from '../../../../../shared/source/query-hooks/learning-opportunities';
import { CountChip } from '../../../design/components/CountChip';
import { InlineLoadingSpinner } from '../../../design/components/LoadingSpinner';
import { SmallText, Text } from '../../../design/components/Typography';
import { useJobsFilterQuery } from '../../query-hooks/jobs';
import { useUsersFilterQuery } from '../../query-hooks/users/users';

import type { GlobalSearchSkill } from '../../../data-contract/global-search';
import type { FunctionComponent, MouseEvent } from 'react';

type SkillsBarChartDataGridCellProps = {
    readonly isRowOpen?: boolean;
    readonly skill: GlobalSearchSkill;
    readonly withLearningOpportunities: boolean;
};

const defaultPagination = {
    page: 0,
    size: 5,
};

type TabName = 'employees' | 'jobs' | 'learningOpportunities';

type ShowMoreParams =
    | { readonly tab: 'employees'; readonly talentDirectoryEmployeeFilters: string }
    | { readonly tab: 'jobs'; readonly talentDirectoryJobFilters: string }
    | { readonly tab: 'learningOpportunities'; readonly talentDirectoryLearningOpportunityFilters: string };

export const SkillsBarChartDataGridCell: FunctionComponent<SkillsBarChartDataGridCellProps> = ({
    isRowOpen,
    skill,
    withLearningOpportunities,
}) => {
    const { id: skillId, jobsWithThisSkill, learningOpportunitiesWithThisSkill, usersWithThisSkill } = skill;
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [menuTitle, setMenuTitle] = useState<string>('');
    const [showMoreParams, setShowMoreParams] = useState<Maybe<ShowMoreParams>>(Maybe.nothing());
    const [menuItems, setMenuItems] = useState<
        readonly { readonly label: string; onClick: () => void }[] | undefined
    >();
    const { actions, translate } = useContext(ApplicationContext);

    const { isFetching: isUsersFilterFetching, refetch: fetchUsersFilter } = useUsersFilterQuery(
        {
            filter: equals('skill', skillId),
            paged: defaultPagination,
        },
        false,
    );

    const { isFetching: isJobsFilterFetching, refetch: fetchJobsFilter } = useJobsFilterQuery({
        enabled: false,
        filter: and(equals('skill', skillId), equals('state', 'published')),
        paged: defaultPagination,
        sorted: {
            fields: [
                {
                    direction: 'desc',
                    field: 'updatedAt',
                },
            ],
        },
    });

    const { isFetching: isLearningOpportunitiesFilterFetching, refetch: fetchLearningOpportunitiesFilter } =
        useLearningOpportunityFilterQuery(
            {
                filter: and(equals('skill', skillId), equals('state', 'published')),
                paged: defaultPagination,
                sorted: {
                    fields: [
                        {
                            direction: 'desc',
                            field: 'updatedAt',
                        },
                    ],
                },
            },
            false,
        );

    const isMenuLoading = isUsersFilterFetching || isLearningOpportunitiesFilterFetching || isJobsFilterFetching;

    const isMenuOpen = Boolean(anchorEl);

    const onMenuOpen = (event: MouseEvent, tabName: TabName, clickable: boolean): void => {
        event.stopPropagation();

        if (!clickable) {
            return;
        }

        setAnchorEl(event.currentTarget);

        const skillFilterValues = JSON.stringify({
            skills: {
                skill: [skillId],
            },
        });

        switch (tabName) {
            case 'employees':
                fetchUsersFilter().then((result) => {
                    setShowMoreParams(
                        Maybe.just({
                            tab: 'employees',
                            talentDirectoryEmployeeFilters: skillFilterValues,
                        }),
                    );
                    setMenuItems(
                        result.data?.data.slice(0, defaultPagination.size).map((user) => ({
                            label: `${user.firstName} ${user.lastName}`.trim(),
                            onClick: () => {
                                actions.navigateTo(`/users/${user.id}`);
                            },
                        })),
                    );
                });
                setMenuTitle(translate('pages.global-search.headlines.employees'));
                break;
            case 'jobs':
                fetchJobsFilter().then((result) => {
                    setShowMoreParams(
                        Maybe.just({
                            tab: 'jobs',
                            talentDirectoryJobFilters: skillFilterValues,
                        }),
                    );
                    setMenuItems(
                        result.data?.data.slice(0, defaultPagination.size).map((job) => ({
                            label: job.title,
                            onClick: () => {
                                actions.navigateTo(`/jobs/${job.id}`);
                            },
                        })),
                    );
                });
                setMenuTitle(translate('pages.global-search.headlines.jobs'));
                break;
            case 'learningOpportunities':
                setShowMoreParams(
                    Maybe.just({
                        tab: 'learningOpportunities',
                        talentDirectoryLearningOpportunityFilters: skillFilterValues,
                    }),
                );

                fetchLearningOpportunitiesFilter().then((result) => {
                    setMenuItems(
                        result.data?.data.slice(0, defaultPagination.size).map((learningOpportunity) => ({
                            label: learningOpportunity.title,
                            onClick: () => {
                                actions.navigateTo(`/learning-opportunities/${learningOpportunity.id}`);
                            },
                        })),
                    );
                });
                setMenuTitle(translate('pages.global-search.headlines.learning-opportunities'));
                break;
            default:
                break;
        }
    };

    const onMenuClose = (): void => {
        setMenuItems(undefined);
        setAnchorEl(null);
    };

    const onShowMore = (): void => {
        if (showMoreParams.isJust) {
            actions.setLocationSearchParams(showMoreParams.value);
        }
    };

    if (isRowOpen === true) {
        return (
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <CountChip
                        ariaLabel="Employees with this skill"
                        backgroundColor="user.main"
                        clickable={usersWithThisSkill > 0}
                        count={usersWithThisSkill}
                        icon={<PeopleAltOutlinedIcon />}
                        onClick={(e) => {
                            onMenuOpen(e, 'employees', usersWithThisSkill > 0);
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <CountChip
                        ariaLabel="Jobs with this skill"
                        backgroundColor="jobposting.main"
                        clickable={jobsWithThisSkill > 0}
                        count={jobsWithThisSkill}
                        icon={<WorkOutlineOutlinedIcon />}
                        onClick={(e) => {
                            onMenuOpen(e, 'jobs', jobsWithThisSkill > 0);
                        }}
                    />
                </Box>
                {withLearningOpportunities && (
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <CountChip
                            ariaLabel="Learning opportunities with this skill"
                            backgroundColor="learningOpportunity.main"
                            clickable={learningOpportunitiesWithThisSkill > 0}
                            count={learningOpportunitiesWithThisSkill}
                            icon={<Shop2OutlinedIcon />}
                            onClick={(e) => {
                                onMenuOpen(e, 'learningOpportunities', learningOpportunitiesWithThisSkill > 0);
                            }}
                        />
                    </Box>
                )}
                <Menu
                    anchorEl={anchorEl}
                    aria-label="Dropdown preview"
                    open={isMenuOpen}
                    sx={{
                        marginTop: 1,
                        width: 220,
                    }}
                    onClose={onMenuClose}
                >
                    <MenuItem
                        disabled={true}
                        sx={{
                            '&.Mui-disabled': {
                                color: 'text.secondary',
                                opacity: 1,
                            },
                            fontSize: '12px',
                            width: 220,
                        }}
                    >
                        <SmallText noWrap={true}>{menuTitle}</SmallText>
                    </MenuItem>
                    {isMenuLoading && <InlineLoadingSpinner />}
                    {menuItems?.map(({ label, onClick }, index) => (
                        <MenuItem key={`${label}-${index}`} aria-label="Preview item" onClick={onClick}>
                            <Text noWrap={true}>{label}</Text>
                        </MenuItem>
                    ))}
                    {is.array(menuItems) && menuItems.length > 4 && (
                        <Fragment>
                            <Divider />
                            <MenuItem
                                aria-label="See more"
                                sx={{ color: 'primary.main', fontSize: '12px', justifyContent: 'flex-end' }}
                                onClick={onShowMore}
                            >
                                {translate('globalsearch.see-more')}
                            </MenuItem>
                        </Fragment>
                    )}
                </Menu>
            </Box>
        );
    }

    return (
        <TalentDirectoryBarChart
            employees={usersWithThisSkill}
            jobs={jobsWithThisSkill}
            learningOpportunities={learningOpportunitiesWithThisSkill}
            withLearningOpportunities={withLearningOpportunities}
        />
    );
};
